// SettlementRecords.tsx
// Route: /admin/Settlementrecord
// Comprehensive Settlement & Vehicle Payout History with Advanced Filters & Excel Export

import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  FaSearch, FaSyncAlt, FaArrowLeft, FaChevronDown, FaChevronUp,
  FaSpinner, FaExclamationTriangle, FaCheckCircle, FaTimesCircle,
  FaFilter, FaMoneyBillWave, FaUser, FaTruck, FaBuilding,
  FaFileInvoiceDollar, FaCalendarAlt, FaFileExcel,
  FaUndo, FaRoute, FaBoxOpen, FaMoneyCheckAlt, FaLayerGroup,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

// ─── TYPES ────────────────────────────────────────────────────────────────────
type PaymentMode = "online" | "cash" | "bank" | "partial";

interface PaymentRecord {
  amount: number;
  paymentMode: PaymentMode;
  date: string;
}

interface InvoiceUserPayment {
  userType: string;
  userName: string;
  amount: number;
  commission?: number;
  tds?: number;
  collection?: number;
  otherDeduction?: number;
  otherDeductionName?: string;
  payableAmount?: number;
  paid: boolean;
  paymentRecords: PaymentRecord[];
}

interface InvoiceItem {
  id: number;
  product: string;
  hsn: string;
  uom: string;
  qty: number;
  rate: number;
  amount: number;
}

interface Invoice {
  id: string;
  lrNumber: string;
  invoiceNumber: string;
  company: string;
  companyAddress?: string;
  date: string;
  sender: string;
  receiver: string;
  broker: string;
  transporter: string;
  driver: string;
  route: string;
  origin: string;
  destination: string;
  vehicle: string;
  payment: number;
  items: InvoiceItem[];
  userDistributions: InvoiceUserPayment[];
  status: "paid" | "partial" | "generated";
}

interface EntityAllocation {
  invoice: Invoice;
  distribution: InvoiceUserPayment;
}

interface EntityGroup {
  key: string;
  userType: string;
  userName: string;
  invoiceCount: number;
  totalGross: number;
  totalCommission: number;
  totalTds: number;
  totalCollection: number;
  totalOtherDeduction: number;
  totalNetPayable: number;
  totalPaid: number;
  totalRemaining: number;
  allocations: EntityAllocation[];
}

type TabKey = "entities" | "trips";

const USER_TYPE_LABELS: Record<string, string> = {
  sender: "Sender", receiver: "Receiver", broker: "Broker",
  driver: "Driver", vehicle: "Vehicle", transporter: "Transporter",
  consignor: "Consignor", gst: "GST", tds: "TDS", profit: "Profit",
};

const MODE_COLORS: Record<PaymentMode, string> = {
  online:  "bg-blue-100 text-blue-800",
  cash:    "bg-green-100 text-green-800",
  bank:    "bg-purple-100 text-purple-800",
  partial: "bg-amber-100 text-amber-800",
};

const TYPE_ICON: Record<string, React.ReactNode> = {
  vehicle:   <FaTruck className="text-orange-500" />,
  driver:    <FaTruck className="text-blue-500" />,
  receiver:  <FaBuilding className="text-purple-500" />,
  sender:    <FaBuilding className="text-indigo-500" />,
  broker:    <FaUser className="text-teal-500" />,
  transporter:<FaTruck className="text-green-500" />,
};

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function safeToString(val: any): string {
  if (val == null) return "";
  if (typeof val === "object") {
    if (val.name) return String(val.name);
    if (val.companyName) return String(val.companyName);
    if (val.company_name) return String(val.company_name);
    if (val.title) return String(val.title);
    return "";
  }
  return String(val);
}

function fmt(n: number) {
  return `₹${Number(n || 0).toLocaleString("en-IN")}`;
}

function fmtDate(d: any): string {
  if (!d) return "—";
  const str = String(d);
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const [, y, m, day] = match;
    const dateObj = new Date(Number(y), Number(m) - 1, Number(day));
    return dateObj.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  }
  try {
    const dt = new Date(d);
    if (!isNaN(dt.getTime())) {
      return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    }
  } catch {}
  return str;
}

function getNetPayable(u: InvoiceUserPayment): number {
  if (u.payableAmount != null && u.payableAmount > 0) return Number(u.payableAmount);
  const gross = Number(u.amount || 0);
  const comm  = Number(u.commission || 0);
  const tds   = Number(u.tds || 0);
  const coll  = Number(u.collection || 0);
  const oth   = Number(u.otherDeduction || 0);
  return Math.max(0, gross - comm - tds - coll - oth);
}

function sumPaid(u: InvoiceUserPayment): number {
  return (u.paymentRecords || []).reduce((a, r) => a + Number(r.amount || 0), 0);
}

const companyCache = new Map<number, string>();
async function fetchCompanyName(id: number): Promise<string> {
  if (!id) return "—";
  if (companyCache.has(id)) return companyCache.get(id)!;
  try {
    const res = await fetch(`${API_BASE}/companyauth/company/${id}`, { credentials: "include" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const name = data.company_name || data.companyName || data.name || data.firm_name || `#${id}`;
    companyCache.set(id, name);
    return name;
  } catch {
    companyCache.set(id, `#${id}`);
    return `#${id}`;
  }
}

const userCache = new Map<number, { name: string; address: string }>();
async function fetchUsersBatch(ids: number[]): Promise<Map<number, { name: string; address: string }>> {
  const uncached = ids.filter((id) => !userCache.has(id));
  if (uncached.length > 0) {
    try {
      const res = await fetch(`${API_BASE}/auth/users/batch?ids=${uncached.join(",")}`, { credentials: "include" });
      if (res.ok) {
        const byId: Record<string, { name: string; address: string }> = await res.json();
        for (const idStr of Object.keys(byId)) {
          const id = Number(idStr);
          userCache.set(id, byId[idStr] || { name: `#${id}`, address: "" });
        }
      }
    } catch {}
    for (const id of uncached) {
      if (!userCache.has(id)) userCache.set(id, { name: `#${id}`, address: "" });
    }
  }
  const result = new Map<number, { name: string; address: string }>();
  ids.forEach((id) => result.set(id, userCache.get(id)!));
  return result;
}

async function fetchDriverNamesBatch(ids: number[]): Promise<Map<number, string>> {
  const result = new Map<number, string>();
  if (ids.length === 0) return result;
  try {
    const res = await fetch(`${API_BASE}/auth/drivers/batch?ids=${ids.join(",")}`, { credentials: "include" });
    const byId: Record<string, string> = res.ok ? await res.json() : {};
    ids.forEach((id) => result.set(id, byId[id] || `#${id}`));
  } catch {
    ids.forEach((id) => result.set(id, `#${id}`));
  }
  return result;
}

async function fetchVehicleNumbersBatch(ids: number[]): Promise<Map<number, string>> {
  const result = new Map<number, string>();
  if (ids.length === 0) return result;
  try {
    const res = await fetch(`${API_BASE}/auth/vehicles/batch?ids=${ids.join(",")}`, { credentials: "include" });
    const byId: Record<string, string> = res.ok ? await res.json() : {};
    ids.forEach((id) => result.set(id, byId[id] || `#${id}`));
  } catch {
    ids.forEach((id) => result.set(id, `#${id}`));
  }
  return result;
}

async function loadSettlementFromDB(invoiceId: string): Promise<InvoiceUserPayment[] | null> {
  try {
    const res = await fetch(`${API_BASE}/settlment/${invoiceId}`, { credentials: "include" });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data || !Array.isArray(data.distributions)) return null;

    return data.distributions.map((d: any) => {
      const grossAmt = Number(d.amount || 0);
      const comm = Number(d.commission || 0);
      const tds = Number(d.tds || 0);
      const coll = Number(d.collection || 0);
      const oth = Number(d.otherDeduction || d.other_deduction || 0);
      const othName = String(d.otherDeductionName || d.other_deduction_name || "");
      const netPayable = d.net_amount != null
        ? Number(d.net_amount)
        : Math.max(0, grossAmt - comm - tds - coll - oth);

      return {
        userType: String(d.user_type || ""),
        userName: String(d.user_name || ""),
        amount: grossAmt,
        commission: comm,
        tds: tds,
        collection: coll,
        otherDeduction: oth,
        otherDeductionName: othName,
        payableAmount: netPayable,
        paid: Boolean(d.paid),
        paymentRecords: Array.isArray(d.paymentRecords)
          ? (d.paymentRecords as Array<Record<string, unknown>>).map((r) => ({
              amount: Number(r.amount || 0),
              paymentMode: String(r.paymentMode || "bank") as PaymentMode,
              date: String(r.date || ""),
            }))
          : [],
      };
    });
  } catch {
    return null;
  }
}

async function fetchAllInvoicesWithSettlements(): Promise<Invoice[]> {
  const [invRes, bulkSettlRes] = await Promise.all([
    fetch(`${API_BASE}/invoiceapi/invoice`, { credentials: "include" }),
    fetch(`${API_BASE}/settlment/all-bulk`, { credentials: "include" }).catch(() => null),
  ]);

  if (!invRes.ok) throw new Error(`HTTP ${invRes.status}: ${invRes.statusText}`);
  const rows: any[] = await invRes.json();
  if (!Array.isArray(rows)) throw new Error("Unexpected response format");

  const bulkMap: Record<string, InvoiceUserPayment[]> = bulkSettlRes && bulkSettlRes.ok ? await bulkSettlRes.json() : {};

  const userIds = new Set<number>();
  const driverIds = new Set<number>();
  const companyIds = new Set<number>();
  const vehicleIds = new Set<number>();

  for (const row of rows) {
    if (typeof row.sender === "number")      userIds.add(row.sender);
    if (typeof row.receiver === "number")    userIds.add(row.receiver);
    if (typeof row.broker === "number")      userIds.add(row.broker);
    if (typeof row.transporter === "number") userIds.add(row.transporter);
    if (typeof row.driver === "number")      driverIds.add(row.driver);
    if (typeof row.company === "number")     companyIds.add(row.company);
    if (typeof row.vehicles === "number")    vehicleIds.add(row.vehicles);
  }

  const [usersMap, driverNamesMap, vehicleNumbersMap] = await Promise.all([
    fetchUsersBatch(Array.from(userIds)),
    fetchDriverNamesBatch(Array.from(driverIds)),
    fetchVehicleNumbersBatch(Array.from(vehicleIds)),
  ]);

  const companyNamesMap = new Map<number, string>();
  await Promise.all(
    Array.from(companyIds).map(async (cid) => {
      const name = await fetchCompanyName(cid);
      companyNamesMap.set(cid, name);
    })
  );

  const mapped: Invoice[] = rows.map((row) => {
    const companyName = row.company && companyNamesMap
      ? (companyNamesMap.get(row.company) ?? `#${row.company}`)
      : "—";
    const sender = usersMap.get(row.sender ?? -1);
    const receiver = usersMap.get(row.receiver ?? -1);
    const broker = usersMap.get(row.broker ?? -1);
    const transporter = usersMap.get(row.transporter ?? -1);
    const driverName = row.driver && driverNamesMap
      ? (driverNamesMap.get(row.driver) ?? `#${row.driver}`)
      : "—";
    const vehicleNo = row.vehicles && vehicleNumbersMap
      ? (vehicleNumbersMap.get(row.vehicles) ?? `#${row.vehicles}`)
      : "—";

    const items: InvoiceItem[] = Array.isArray(row.items)
      ? row.items.map((it: any) => ({
          id: Number(it.id || 0),
          product: safeToString(it.product || it.items || it.name || "Item"),
          hsn: safeToString(it.hsn || ""),
          uom: safeToString(it.uom || "—"),
          qty: Number(it.qty || it.weight || 0),
          rate: Number(it.rate || 0),
          amount: Number(it.amount || 0),
        }))
      : [];

    const inv: Invoice = {
      id: String(row.id),
      lrNumber: String(row.lr_no || row.lrNumber || ""),
      invoiceNumber: String(row.invoiceno || row.invoiceNo || ""),
      company: companyName,
      date: row.date || row.created_at || "",
      sender: sender ? sender.name : "—",
      receiver: receiver ? receiver.name : "—",
      broker: broker ? broker.name : "—",
      transporter: transporter ? transporter.name : "—",
      driver: driverName,
      vehicle: vehicleNo,
      route: row.source && row.destination ? `${row.source} → ${row.destination}` : "—",
      origin: row.source || "—",
      destination: row.destination || "—",
      payment: Number(row.payment || 0),
      items,
      userDistributions: [],
      status: (row.paymentStatus || "").toLowerCase() === "paid" ? "paid" : "generated",
    };

    if (bulkMap[inv.id] && bulkMap[inv.id].length > 0) {
      inv.userDistributions = bulkMap[inv.id];
      const allPaid = inv.userDistributions.every((d) => d.paid);
      const anyPaid = inv.userDistributions.some((d) => sumPaid(d) > 0);
      inv.status = allPaid ? "paid" : anyPaid ? "partial" : "generated";
    }

    return inv;
  });

  return mapped;
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const SettlementRecords: React.FC = () => {
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);

  const [tab, setTab]                   = useState<TabKey>("entities");
  const [expandedKey, setExpandedKey]   = useState<string | null>(null);

  // Advanced Filters State
  const [showFilters, setShowFilters]                 = useState(false);
  const [search, setSearch]                           = useState("");
  const [filterCompany, setFilterCompany]             = useState("");
  const [filterParty, setFilterParty]                 = useState("");
  const [filterVehicle, setFilterVehicle]             = useState("");
  const [filterUserType, setFilterUserType]           = useState("");
  const [filterStatus, setFilterStatus]               = useState("");
  const [filterDeductionType, setFilterDeductionType] = useState("");
  const [filterDateFrom, setFilterDateFrom]           = useState("");
  const [filterDateTo, setFilterDateTo]               = useState("");

  const activeFilterCount = [
    filterCompany, filterParty, filterVehicle, filterUserType,
    filterStatus, filterDeductionType, filterDateFrom, filterDateTo,
  ].filter(Boolean).length;

  const resetFilters = () => {
    setSearch("");
    setFilterCompany("");
    setFilterParty("");
    setFilterVehicle("");
    setFilterUserType("");
    setFilterStatus("");
    setFilterDeductionType("");
    setFilterDateFrom("");
    setFilterDateTo("");
  };

  const loadData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await fetchAllInvoicesWithSettlements();
      setInvoices(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load settlement records");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Unique Dropdown Options
  const uniqueCompanies = useMemo(() => {
    const set = new Set<string>();
    invoices.forEach((inv) => {
      const c = safeToString(inv.company);
      if (c && c !== "—") set.add(c);
    });
    return Array.from(set).sort();
  }, [invoices]);

  const uniqueParties = useMemo(() => {
    const set = new Set<string>();
    invoices.forEach((inv) => {
      [inv.sender, inv.receiver, inv.transporter, inv.driver, inv.broker].forEach((p) => {
        const str = safeToString(p);
        if (str && str !== "—") set.add(str);
      });
      inv.userDistributions.forEach((d) => {
        const u = safeToString(d.userName);
        if (u && u !== "—") set.add(u);
      });
    });
    return Array.from(set).sort();
  }, [invoices]);

  const uniqueVehicles = useMemo(() => {
    const set = new Set<string>();
    invoices.forEach((inv) => {
      const v = safeToString(inv.vehicle);
      if (v && v !== "—") set.add(v);
    });
    return Array.from(set).sort();
  }, [invoices]);

  // Filtered Invoices
  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      const searchStr = `${safeToString(inv.lrNumber)} ${safeToString(inv.invoiceNumber)} ${safeToString(inv.company)} ${safeToString(inv.origin)} ${safeToString(inv.destination)} ${safeToString(inv.sender)} ${safeToString(inv.receiver)} ${safeToString(inv.driver)} ${safeToString(inv.vehicle)} ${safeToString(inv.broker)}`.toLowerCase();
      if (search && !searchStr.includes(search.toLowerCase())) return false;

      if (filterCompany && safeToString(inv.company).toLowerCase() !== filterCompany.toLowerCase()) return false;
      if (filterVehicle && safeToString(inv.vehicle).toLowerCase() !== filterVehicle.toLowerCase()) return false;

      if (filterParty) {
        const pTarget = filterParty.toLowerCase();
        const match =
          safeToString(inv.sender).toLowerCase() === pTarget ||
          safeToString(inv.receiver).toLowerCase() === pTarget ||
          safeToString(inv.transporter).toLowerCase() === pTarget ||
          safeToString(inv.driver).toLowerCase() === pTarget ||
          safeToString(inv.broker).toLowerCase() === pTarget ||
          inv.userDistributions.some((d) => safeToString(d.userName).toLowerCase() === pTarget);
        if (!match) return false;
      }

      if (filterStatus) {
        if (inv.status !== filterStatus) return false;
      }

      if (filterUserType) {
        const hasType = inv.userDistributions.some((d) => d.userType === filterUserType) ||
          (filterUserType === "driver" && inv.driver && inv.driver !== "—") ||
          (filterUserType === "vehicle" && inv.vehicle && inv.vehicle !== "—") ||
          (filterUserType === "broker" && inv.broker && inv.broker !== "—") ||
          (filterUserType === "receiver" && inv.receiver && inv.receiver !== "—") ||
          (filterUserType === "sender" && inv.sender && inv.sender !== "—");
        if (!hasType) return false;
      }

      if (filterDateFrom) {
        const d = inv.date ? inv.date.slice(0, 10) : "";
        if (!d || d < filterDateFrom) return false;
      }
      if (filterDateTo) {
        const d = inv.date ? inv.date.slice(0, 10) : "";
        if (!d || d > filterDateTo) return false;
      }

      if (filterDeductionType) {
        if (filterDeductionType === "any") {
          const hasD = inv.userDistributions.some((d) =>
            ((d.commission || 0) + (d.tds || 0) + (d.collection || 0) + (d.otherDeduction || 0)) > 0
          );
          if (!hasD) return false;
        } else if (filterDeductionType === "commission") {
          if (!inv.userDistributions.some((d) => (d.commission || 0) > 0)) return false;
        } else if (filterDeductionType === "tds") {
          if (!inv.userDistributions.some((d) => (d.tds || 0) > 0)) return false;
        } else if (filterDeductionType === "collection") {
          if (!inv.userDistributions.some((d) => (d.collection || 0) > 0)) return false;
        } else if (filterDeductionType === "other") {
          if (!inv.userDistributions.some((d) => (d.otherDeduction || 0) > 0)) return false;
        }
      }

      return true;
    });
  }, [invoices, search, filterCompany, filterParty, filterVehicle, filterUserType, filterStatus, filterDeductionType, filterDateFrom, filterDateTo]);

  // Group Allocations by Entity (Vehicle / Person)
  const entityGroups = useMemo(() => {
    const map = new Map<string, EntityGroup>();

    filteredInvoices.forEach((inv) => {
      inv.userDistributions.forEach((dist) => {
        const name = safeToString(dist.userName);
        if (!name || name === "—") return;
        const key = `${dist.userType}::${name}`;

        const comm = Number(dist.commission || 0);
        const tds  = Number(dist.tds || 0);
        const coll = Number(dist.collection || 0);
        const oth  = Number(dist.otherDeduction || 0);
        const gross = Number(dist.amount || 0);
        const net  = getNetPayable(dist);
        const paid = sumPaid(dist);
        const rem  = Math.max(0, net - paid);

        if (!map.has(key)) {
          map.set(key, {
            key,
            userType: dist.userType,
            userName: name,
            invoiceCount: 0,
            totalGross: 0,
            totalCommission: 0,
            totalTds: 0,
            totalCollection: 0,
            totalOtherDeduction: 0,
            totalNetPayable: 0,
            totalPaid: 0,
            totalRemaining: 0,
            allocations: [],
          });
        }

        const grp = map.get(key)!;
        grp.invoiceCount += 1;
        grp.totalGross += gross;
        grp.totalCommission += comm;
        grp.totalTds += tds;
        grp.totalCollection += coll;
        grp.totalOtherDeduction += oth;
        grp.totalNetPayable += net;
        grp.totalPaid += paid;
        grp.totalRemaining += rem;
        grp.allocations.push({ invoice: inv, distribution: dist });
      });
    });

    return Array.from(map.values()).sort((a, b) => b.totalNetPayable - a.totalNetPayable);
  }, [filteredInvoices]);

  // Total Summary KPI Metrics
  const summaryMetrics = useMemo(() => {
    let gross = 0, comm = 0, tds = 0, coll = 0, oth = 0, net = 0, paid = 0, rem = 0;
    entityGroups.forEach((g) => {
      gross += g.totalGross;
      comm  += g.totalCommission;
      tds   += g.totalTds;
      coll  += g.totalCollection;
      oth   += g.totalOtherDeduction;
      net   += g.totalNetPayable;
      paid  += g.totalPaid;
      rem   += g.totalRemaining;
    });
    return { gross, comm, tds, coll, oth, net, paid, rem, entityCount: entityGroups.length };
  }, [entityGroups]);

  // Export to Excel / CSV Function (supports all or single vehicle target)
  const exportToExcel = (targetGroups?: EntityGroup[], customFilename?: string) => {
    const groupsToExport = targetGroups || entityGroups;
    if (groupsToExport.length === 0) return;

    const headers = [
      "Company Name (Hired By)",
      "Vehicle / Entity Name",
      "User Type",
      "LR Number",
      "Invoice Number",
      "Trip Date",
      "Origin",
      "Destination",
      "Sender Name",
      "Receiver Name",
      "Items Summary",
      "Gross Allocated (₹)",
      "Commission (₹)",
      "TDS (₹)",
      "Collection (₹)",
      "Other Deduction Name",
      "Other Deduction Amount (₹)",
      "Total Deductions (₹)",
      "Net Payable (₹)",
      "Total Paid (₹)",
      "Remaining Due (₹)",
      "Payout Status",
      "Payment History Logs",
    ];

    const rows: string[][] = [];

    groupsToExport.forEach((grp) => {
      grp.allocations.forEach(({ invoice, distribution: dist }) => {
        const comp    = safeToString(invoice.company) || "—";
        const entity  = safeToString(dist.userName) || "—";
        const uType   = USER_TYPE_LABELS[dist.userType] || dist.userType;
        const lrNo    = safeToString(invoice.lrNumber) || "—";
        const invNo   = safeToString(invoice.invoiceNumber) || "—";
        const tDate   = invoice.date ? invoice.date.slice(0, 10) : "—";
        const orig    = safeToString(invoice.origin) || "—";
        const dest    = safeToString(invoice.destination) || "—";
        const sender  = safeToString(invoice.sender) || "—";
        const recv    = safeToString(invoice.receiver) || "—";

        const itemsStr = invoice.items
          .map((it) => `${safeToString(it.product)} (${it.qty || 0} ${it.uom !== "—" ? it.uom : ""} @ ₹${it.rate || 0} = ₹${it.amount || 0})`)
          .join(" | ");

        const gross   = Number(dist.amount || 0);
        const comm    = Number(dist.commission || 0);
        const tdsVal  = Number(dist.tds || 0);
        const collVal = Number(dist.collection || 0);
        const othVal  = Number(dist.otherDeduction || 0);
        const othName = dist.otherDeductionName || "";
        const totDed  = comm + tdsVal + collVal + othVal;
        const net     = getNetPayable(dist);
        const pAmt    = sumPaid(dist);
        const remAmt  = Math.max(0, net - pAmt);
        const status  = pAmt >= net && net > 0 ? "Fully Paid" : pAmt > 0 ? "Partially Paid" : "Unpaid";

        const pHistory = (dist.paymentRecords || [])
          .map((r) => `${r.date ? r.date.slice(0, 10) : ""}: ₹${r.amount} (${r.paymentMode})`)
          .join(" ; ");

        rows.push([
          comp, entity, uType, lrNo, invNo, tDate, orig, dest, sender, recv, itemsStr,
          gross.toString(), comm.toString(), tdsVal.toString(), collVal.toString(),
          othName, othVal.toString(), totDed.toString(), net.toString(),
          pAmt.toString(), remAmt.toString(), status, pHistory,
        ]);
      });
    });

    const escapeCSV = (val: string) => {
      if (val.includes('"') || val.includes(",") || val.includes("\n")) {
        return `"${val.replace(/"/g, '""')}"`;
      }
      return val;
    };

    const csvContent = "\uFEFF" + [
      headers.map(escapeCSV).join(","),
      ...rows.map((r) => r.map(escapeCSV).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const fileName = customFilename || (targetGroups && targetGroups.length === 1
      ? `Settlement_Report_${targetGroups[0].userName.replace(/[^a-zA-Z0-9_-]/g, "_")}_${new Date().toISOString().slice(0, 10)}.csv`
      : `Settlement_Vehicle_Full_Report_${new Date().toISOString().slice(0, 10)}.csv`);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-100 p-4 sm:p-6 text-slate-900">
      <div className="max-w-none w-full mx-auto space-y-6">

        {/* TOP HEADER */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-xs">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin/settlement")}
              className="p-2.5 rounded-xl bg-slate-100 border border-slate-300 text-slate-700 hover:bg-blue-600 hover:text-white transition shadow-2xs"
            >
              <FaArrowLeft />
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight flex items-center gap-2">
                Settlement & Vehicle Trip Records
              </h1>
              <p className="text-slate-500 font-medium text-xs sm:text-sm mt-0.5">
                Complete audit trail of vehicle allocations, company hires, route breakdowns, items, deductions & payments
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => exportToExcel()}
              disabled={entityGroups.length === 0}
              className="px-4 py-2 rounded-xl bg-emerald-600 text-white font-bold text-xs sm:text-sm hover:bg-black transition shadow-xs disabled:opacity-50 flex items-center gap-2"
            >
              <FaFileExcel className="text-lg" /> Export Excel CSV
            </button>
            <button
              onClick={() => setShowFilters((p) => !p)}
              className={`px-4 py-2 rounded-xl font-bold text-xs sm:text-sm transition flex items-center gap-2 border shadow-xs ${
                showFilters || activeFilterCount > 0
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              <FaFilter /> Advance Filters
              {activeFilterCount > 0 && (
                <span className="bg-white text-blue-800 rounded-full text-[11px] px-2 py-0.5 font-black">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <button
              onClick={loadData}
              disabled={loading}
              className="px-4 py-2 rounded-xl bg-blue-600 text-white font-bold text-xs sm:text-sm hover:bg-black transition shadow-xs disabled:opacity-60 flex items-center gap-2"
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaSyncAlt />} Refresh
            </button>
          </div>
        </div>

        {/* SEARCH & ADVANCED FILTERS BAR */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-4 space-y-4">
          <div className="flex flex-wrap gap-3 items-center justify-between">
            <div className="relative flex-1 min-w-[260px]">
              <FaSearch className="absolute top-1/2 left-3.5 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                type="text"
                placeholder="Search Vehicle No / Driver / Company / LR# / Route / Item…"
                className="pl-10 pr-4 py-2 rounded-xl border border-slate-300 text-xs sm:text-sm text-slate-800 font-semibold w-full bg-slate-50 focus:bg-white focus:border-blue-500 focus:outline-none"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {(activeFilterCount > 0 || search) && (
              <button
                onClick={resetFilters}
                className="px-3.5 py-2 bg-slate-100 hover:bg-red-50 text-red-600 border border-slate-300 rounded-xl font-bold text-xs sm:text-sm transition flex items-center gap-1.5"
              >
                <FaUndo /> Reset All Filters
              </button>
            )}
          </div>

          {/* Expandable Advance Filter Dropdowns */}
          {showFilters && (
            <div className="pt-3 border-t border-slate-200 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 text-xs">
              <div>
                <label className="block font-bold text-slate-700 mb-1">Company (Hired By)</label>
                <select
                  value={filterCompany}
                  onChange={(e) => setFilterCompany(e.target.value)}
                  className="rounded-xl border border-slate-300 px-2.5 py-1.5 font-semibold text-slate-800 bg-slate-50 focus:border-blue-500 focus:outline-none w-full"
                >
                  <option value="">All Companies</option>
                  {uniqueCompanies.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Party / Person</label>
                <select
                  value={filterParty}
                  onChange={(e) => setFilterParty(e.target.value)}
                  className="rounded-xl border border-slate-300 px-2.5 py-1.5 font-semibold text-slate-800 bg-slate-50 focus:border-blue-500 focus:outline-none w-full"
                >
                  <option value="">All Parties</option>
                  {uniqueParties.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Vehicle No.</label>
                <select
                  value={filterVehicle}
                  onChange={(e) => setFilterVehicle(e.target.value)}
                  className="rounded-xl border border-slate-300 px-2.5 py-1.5 font-semibold text-slate-800 bg-slate-50 focus:border-blue-500 focus:outline-none w-full"
                >
                  <option value="">All Vehicles</option>
                  {uniqueVehicles.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">User Type</label>
                <select
                  value={filterUserType}
                  onChange={(e) => setFilterUserType(e.target.value)}
                  className="rounded-xl border border-slate-300 px-2.5 py-1.5 font-semibold text-slate-800 bg-slate-50 focus:border-blue-500 focus:outline-none capitalize w-full"
                >
                  <option value="">All Types</option>
                  <option value="vehicle">Vehicle</option>
                  <option value="driver">Driver</option>
                  <option value="broker">Broker</option>
                  <option value="receiver">Receiver</option>
                  <option value="sender">Sender</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Payout Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="rounded-xl border border-slate-300 px-2.5 py-1.5 font-semibold text-slate-800 bg-slate-50 focus:border-blue-500 focus:outline-none w-full"
                >
                  <option value="">All Statuses</option>
                  <option value="paid">Fully Paid</option>
                  <option value="partial">Partially Paid</option>
                  <option value="generated">Unsettled (Pending)</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Deductions</label>
                <select
                  value={filterDeductionType}
                  onChange={(e) => setFilterDeductionType(e.target.value)}
                  className="rounded-xl border border-slate-300 px-2.5 py-1.5 font-semibold text-slate-800 bg-slate-50 focus:border-blue-500 focus:outline-none w-full"
                >
                  <option value="">All Invoices</option>
                  <option value="any">Has Any Deductions</option>
                  <option value="commission">Commission &gt; ₹0</option>
                  <option value="tds">TDS &gt; ₹0</option>
                  <option value="collection">Collection &gt; ₹0</option>
                  <option value="other">Other Deduction &gt; ₹0</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-slate-700 mb-1">Date Range</label>
                <div className="flex gap-1 items-center">
                  <input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    className="rounded-lg border border-slate-300 px-1.5 py-1 font-semibold text-[11px] text-slate-800 bg-slate-50 w-full"
                  />
                  <span className="text-slate-400 font-bold">→</span>
                  <input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    className="rounded-lg border border-slate-300 px-1.5 py-1 font-semibold text-[11px] text-slate-800 bg-slate-50 w-full"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* SUMMARY KPI METRICS CARDS */}
        {entityGroups.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 text-xs sm:text-sm">
            <div className="bg-white p-3.5 rounded-2xl border border-slate-200 shadow-2xs">
              <span className="text-[11px] text-slate-500 font-bold uppercase block">Entities / Vehicles</span>
              <span className="font-black text-slate-900 text-base sm:text-lg mt-0.5 block">{summaryMetrics.entityCount}</span>
            </div>
            <div className="bg-blue-50/90 p-3.5 rounded-2xl border border-blue-100 shadow-2xs">
              <span className="text-[11px] text-blue-700 font-bold uppercase block">Gross Allocated</span>
              <span className="font-extrabold text-blue-950 text-base sm:text-lg mt-0.5 block">{fmt(summaryMetrics.gross)}</span>
            </div>
            <div className="bg-red-50/90 p-3.5 rounded-2xl border border-red-100 shadow-2xs">
              <span className="text-[11px] text-red-600 font-bold uppercase block">Total Deductions</span>
              <span className="font-extrabold text-red-700 text-base sm:text-lg mt-0.5 block">
                -{fmt(summaryMetrics.comm + summaryMetrics.tds + summaryMetrics.coll + summaryMetrics.oth)}
              </span>
            </div>
            <div className="bg-indigo-50/90 p-3.5 rounded-2xl border border-indigo-100 shadow-2xs">
              <span className="text-[11px] text-indigo-700 font-bold uppercase block">Net Payable</span>
              <span className="font-black text-indigo-950 text-base sm:text-lg mt-0.5 block">{fmt(summaryMetrics.net)}</span>
            </div>
            <div className="bg-green-50/90 p-3.5 rounded-2xl border border-green-100 shadow-2xs">
              <span className="text-[11px] text-green-700 font-bold uppercase block">Total Paid</span>
              <span className="font-extrabold text-green-800 text-base sm:text-lg mt-0.5 block">{fmt(summaryMetrics.paid)}</span>
            </div>
            <div className="bg-amber-50/90 p-3.5 rounded-2xl border border-amber-100 shadow-2xs">
              <span className="text-[11px] text-amber-700 font-bold uppercase block">Remaining Due</span>
              <span className="font-extrabold text-amber-800 text-base sm:text-lg mt-0.5 block">{fmt(summaryMetrics.rem)}</span>
            </div>
            <div className="bg-slate-50 p-3.5 rounded-2xl border border-slate-200 shadow-2xs col-span-2 sm:col-span-1">
              <span className="text-[11px] text-slate-600 font-bold uppercase block">Payout Rate</span>
              <span className="font-black text-slate-900 text-base sm:text-lg mt-0.5 block">
                {summaryMetrics.net > 0 ? `${Math.round((summaryMetrics.paid / summaryMetrics.net) * 100)}%` : "100%"}
              </span>
            </div>
          </div>
        )}

        {/* LOADING & ERROR STATES */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-slate-200 shadow-xs">
            <FaSpinner className="text-blue-600 text-4xl animate-spin mb-3" />
            <span className="text-slate-700 font-bold text-base">Loading detailed settlement records…</span>
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-slate-200 shadow-xs p-6">
            <FaExclamationTriangle className="text-red-500 text-4xl mb-3" />
            <div className="text-red-700 font-bold text-base text-center mb-4">{error}</div>
            <button onClick={loadData} className="px-5 py-2 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-black transition">
              Retry Loading
            </button>
          </div>
        )}

        {/* CONTENT RECORDS LIST */}
        {!loading && !error && (
          <div className="space-y-4">
            {entityGroups.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400 font-medium text-sm">
                No matching vehicle or entity settlement records found. Try adjusting filters or search.
              </div>
            ) : (
              entityGroups.map((grp) => {
                const isExpanded = expandedKey === grp.key;
                const pctPaid = grp.totalNetPayable > 0
                  ? Math.round((grp.totalPaid / grp.totalNetPayable) * 100)
                  : 100;
                const isFullyPaid = grp.totalRemaining === 0 && grp.totalNetPayable > 0;

                return (
                  <div
                    key={grp.key}
                    className={`bg-white rounded-2xl border-2 shadow-2xs transition overflow-hidden ${
                      isFullyPaid ? "border-green-300" : "border-slate-200"
                    }`}
                  >
                    {/* ENTITY SUMMARY ROW */}
                    <button
                      type="button"
                      className="w-full p-4 sm:p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4 text-left hover:bg-slate-50/80 transition"
                      onClick={() => setExpandedKey(isExpanded ? null : grp.key)}
                    >
                      <div className="flex items-center gap-3.5 flex-1 min-w-0">
                        <div className="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center text-2xl shrink-0">
                          {TYPE_ICON[grp.userType] || <FaUser className="text-blue-600" />}
                        </div>
                        <div className="min-w-0">
                          <div className="font-black text-slate-900 text-base sm:text-lg truncate flex items-center gap-2">
                            <span>{grp.userName}</span>
                            <span className="text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-0.5 rounded-lg capitalize">
                              {USER_TYPE_LABELS[grp.userType] || grp.userType}
                            </span>
                            <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-lg">
                              {grp.invoiceCount} Trip{grp.invoiceCount !== 1 ? "s" : ""}
                            </span>
                          </div>
                          <div className="text-xs text-slate-500 font-medium mt-1 flex flex-wrap items-center gap-3">
                            <span>Gross: <b>{fmt(grp.totalGross)}</b></span>
                            <span>Deductions: <b className="text-red-700">-{fmt(grp.totalCommission + grp.totalTds + grp.totalCollection + grp.totalOtherDeduction)}</b></span>
                            <span>Net Payable: <b className="text-indigo-950">{fmt(grp.totalNetPayable)}</b></span>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-xs sm:text-sm shrink-0 border-t lg:border-t-0 pt-3 lg:pt-0">
                        <div className="text-center">
                          <span className="text-[11px] text-slate-500 font-bold uppercase block">Paid</span>
                          <span className="font-extrabold text-green-700 text-sm sm:text-base">{fmt(grp.totalPaid)}</span>
                        </div>

                        <div className="text-center">
                          <span className="text-[11px] text-slate-500 font-bold uppercase block">Remaining</span>
                          <span className={`font-extrabold text-sm sm:text-base ${grp.totalRemaining > 0 ? "text-amber-700" : "text-green-700"}`}>
                            {fmt(grp.totalRemaining)}
                          </span>
                        </div>

                        <div className="flex items-center gap-3 shrink-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              exportToExcel([grp]);
                            }}
                            className="px-3 py-1.5 rounded-xl bg-emerald-50 hover:bg-emerald-600 hover:text-white border border-emerald-300 text-emerald-800 font-bold text-xs transition flex items-center gap-1.5 shrink-0 shadow-2xs"
                            title={`Download CSV report for ${grp.userName}`}
                          >
                            <FaFileExcel className="text-emerald-700 text-sm" />
                            <span className="hidden sm:inline">Download</span> CSV
                          </button>

                          <div className="flex-1 min-w-[80px]">
                            <div className="text-[10px] font-bold text-slate-500 mb-0.5">{pctPaid}% paid</div>
                            <div className="h-2 rounded-full bg-slate-100 overflow-hidden w-20 sm:w-24 border border-slate-200">
                              <div
                                className={`h-full rounded-full transition-all ${pctPaid >= 100 ? "bg-green-600" : pctPaid > 0 ? "bg-blue-600" : "bg-slate-300"}`}
                                style={{ width: `${Math.min(100, pctPaid)}%` }}
                              />
                            </div>
                          </div>
                          {isExpanded ? <FaChevronUp className="text-blue-600" /> : <FaChevronDown className="text-slate-400" />}
                        </div>
                      </div>
                    </button>

                    {/* EXPANDED DETAILED TRIPS LIST */}
                    {isExpanded && (
                      <div className="border-t border-slate-200 p-4 sm:p-5 bg-slate-50/60 space-y-4">
                        <div className="text-xs font-bold text-slate-700 uppercase tracking-wide flex flex-wrap items-center justify-between gap-2">
                          <span>Detailed Trip Allocations & Hired Companies ({grp.allocations.length})</span>
                          <button
                            type="button"
                            onClick={() => exportToExcel([grp])}
                            className="px-3 py-1 rounded-lg bg-emerald-600 hover:bg-black text-white font-bold text-xs transition flex items-center gap-1.5 shadow-2xs"
                          >
                            <FaFileExcel /> Download {grp.userName} CSV
                          </button>
                        </div>

                        <div className="space-y-4">
                          {grp.allocations.map(({ invoice: inv, distribution: dist }, aIdx) => {
                            const net      = getNetPayable(dist);
                            const paid     = sumPaid(dist);
                            const rem      = Math.max(0, net - paid);
                            const isPaid   = paid >= net && net > 0;
                            const totalDed = (dist.commission || 0) + (dist.tds || 0) + (dist.collection || 0) + (dist.otherDeduction || 0);

                            return (
                              <div
                                key={`${inv.id}_${aIdx}`}
                                className="bg-white rounded-xl border border-slate-200 p-4 shadow-2xs space-y-3"
                              >
                                {/* Trip Header Info */}
                                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-3">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className="font-extrabold text-sm sm:text-base text-slate-900 capitalize">
                                      🏢 Hired By: <span className="text-blue-700">{safeToString(inv.company)}</span>
                                    </span>
                                    <span className="text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded">
                                      LR: #{safeToString(inv.lrNumber)}
                                    </span>
                                    <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                                      Inv: #{safeToString(inv.invoiceNumber)}
                                    </span>
                                    {inv.date && (
                                      <span className="text-xs font-medium text-slate-500">
                                        📅 {fmtDate(inv.date)}
                                      </span>
                                    )}
                                  </div>

                                  <div className="flex items-center gap-2">
                                    <span className={`px-2.5 py-0.5 rounded-lg text-xs font-bold ${
                                      isPaid ? "bg-green-100 text-green-800" : paid > 0 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"
                                    }`}>
                                      {isPaid ? "Fully Paid" : paid > 0 ? "Partially Paid" : "Unpaid"}
                                    </span>
                                  </div>
                                </div>

                                {/* Route & Parties Badges */}
                                <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-200/80 text-xs grid grid-cols-1 sm:grid-cols-3 gap-2">
                                  <div className="flex items-center gap-1.5 text-slate-800 font-bold">
                                    <FaRoute className="text-blue-600 text-sm" />
                                    <span>Route: <b className="text-slate-900">{safeToString(inv.route)}</b></span>
                                  </div>
                                  <div className="truncate">
                                    <b className="text-slate-500">Sender:</b> <span className="font-semibold text-slate-800">{safeToString(inv.sender)}</span>
                                  </div>
                                  <div className="truncate">
                                    <b className="text-slate-500">Receiver:</b> <span className="font-semibold text-slate-800">{safeToString(inv.receiver)}</span>
                                  </div>
                                </div>

                                {/* Items Breakdown Table */}
                                {inv.items && inv.items.length > 0 && (
                                  <div className="border border-slate-200 rounded-xl overflow-x-auto bg-white">
                                    <table className="w-full text-xs">
                                      <thead className="bg-slate-100 text-slate-800 font-bold border-b">
                                        <tr>
                                          <th className="p-1.5 text-center">#</th>
                                          <th className="p-1.5 text-left">Product / Goods Item</th>
                                          <th className="p-1.5 text-center">HSN</th>
                                          <th className="p-1.5 text-center">Qty / Weight</th>
                                          <th className="p-1.5 text-center">Rate (₹)</th>
                                          <th className="p-1.5 text-right">Amount (₹)</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {inv.items.map((it, iIdx) => (
                                          <tr key={it.id || iIdx} className="border-b border-slate-100">
                                            <td className="p-1.5 text-center text-slate-400">{iIdx + 1}</td>
                                            <td className="p-1.5 font-bold text-blue-900">{safeToString(it.product)}</td>
                                            <td className="p-1.5 text-center text-slate-600">{safeToString(it.hsn)}</td>
                                            <td className="p-1.5 text-center font-bold text-indigo-900">
                                              {it.qty ? `${it.qty} ${it.uom !== "—" ? it.uom : ""}` : "—"}
                                            </td>
                                            <td className="p-1.5 text-center">{it.rate ? fmt(it.rate) : "—"}</td>
                                            <td className="p-1.5 font-bold text-green-700 text-right">{it.amount ? fmt(it.amount) : "—"}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}

                                {/* Deduction Details & Metrics Bar */}
                                <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2 text-xs">
                                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 pb-2">
                                    <div className="font-bold text-slate-800">
                                      Gross Allocated: <span className="font-extrabold text-blue-900 text-sm ml-1">{fmt(dist.amount)}</span>
                                    </div>

                                    <div className="flex flex-wrap items-center gap-1.5">
                                      <span className="text-[11px] font-bold text-red-700 uppercase tracking-wider mr-1">Deductions:</span>
                                      {(dist.commission || 0) > 0 && (
                                        <span className="bg-red-100 text-red-800 border border-red-200 px-2 py-0.5 rounded font-bold">
                                          Commission: -{fmt(dist.commission!)}
                                        </span>
                                      )}
                                      {(dist.tds || 0) > 0 && (
                                        <span className="bg-red-100 text-red-800 border border-red-200 px-2 py-0.5 rounded font-bold">
                                          TDS: -{fmt(dist.tds!)}
                                        </span>
                                      )}
                                      {(dist.collection || 0) > 0 && (
                                        <span className="bg-red-100 text-red-800 border border-red-200 px-2 py-0.5 rounded font-bold">
                                          Collection: -{fmt(dist.collection!)}
                                        </span>
                                      )}
                                      {(dist.otherDeduction || 0) > 0 && (
                                        <span className="bg-red-100 text-red-800 border border-red-200 px-2 py-0.5 rounded font-bold">
                                          {dist.otherDeductionName?.trim() || "Other"}: -{fmt(dist.otherDeduction!)}
                                        </span>
                                      )}
                                      {totalDed === 0 && (
                                        <span className="text-slate-400 italic">None (₹0)</span>
                                      )}

                                      <span className="bg-red-600 text-white font-extrabold px-2 py-0.5 rounded shadow-2xs ml-1">
                                        Total Deducted: -{fmt(totalDed)}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-3 gap-2 pt-1 font-bold">
                                    <div className="bg-indigo-50 p-2 rounded-lg border border-indigo-100">
                                      <span className="text-[10px] text-indigo-700 block uppercase">Net Payable</span>
                                      <span className="text-indigo-950 text-sm">{fmt(net)}</span>
                                    </div>
                                    <div className="bg-green-50 p-2 rounded-lg border border-green-100">
                                      <span className="text-[10px] text-green-700 block uppercase">Total Paid</span>
                                      <span className="text-green-800 text-sm">{fmt(paid)}</span>
                                    </div>
                                    <div className="bg-amber-50 p-2 rounded-lg border border-amber-100">
                                      <span className="text-[10px] text-amber-700 block uppercase">Remaining Due</span>
                                      <span className="text-amber-800 text-sm">{fmt(rem)}</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Payment History Transactions */}
                                {dist.paymentRecords && dist.paymentRecords.length > 0 && (
                                  <div className="pt-2">
                                    <div className="text-[11px] font-bold text-slate-600 mb-1">
                                      Payment Transactions History ({dist.paymentRecords.length})
                                    </div>
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-xs border bg-slate-50/50 rounded-lg">
                                        <thead>
                                          <tr className="bg-slate-100 text-slate-700 font-bold border-b">
                                            <th className="p-1.5 text-left">Date</th>
                                            <th className="p-1.5 text-left">Amount</th>
                                            <th className="p-1.5 text-left">Payment Mode</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {dist.paymentRecords.map((rec, pIdx) => (
                                            <tr key={pIdx} className="border-b border-slate-200/60 bg-white">
                                              <td className="p-1.5 font-medium text-slate-800">{fmtDate(rec.date)}</td>
                                              <td className="p-1.5 font-bold text-green-700">{fmt(rec.amount)}</td>
                                              <td className="p-1.5">
                                                <span className={`px-2 py-0.5 rounded text-[11px] font-bold capitalize ${MODE_COLORS[rec.paymentMode] || "bg-slate-100 text-slate-700"}`}>
                                                  {rec.paymentMode}
                                                </span>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SettlementRecords;